// MARK://dhk8/pos/features/billing/invoice-attempts-timeline
// Lane #175.B.1 — Timeline de intents de facturación para un ticket.
//
// Se inserta en `/tickets/:orderId` (ReceiptPage) bajo la sección de
// facturación. Sin facturas registradas el componente NO renderiza nada
// (silencioso). Cuando hay actividad, muestra:
//
//   1. Badge alerta amber si hubo stamp_error reciente (cliente intentó
//      facturar y falló — el staff necesita saberlo).
//   2. Lista cronológica DESC: timestamp, stage, RFC capturado, resultado.
//      Errores muestran el código + mensaje técnico (que el cliente NO ve).
//
// Refresh manual al expandir. Sin polling — la actividad es del lado cliente.

import { useEffect, useState } from 'react'
import { AlertTriangle, Eye, FileEdit, UserPlus, CheckCircle2, XCircle, AlertOctagon, RefreshCw } from 'lucide-react'

import { listInvoiceAttempts, type InvoiceAttemptDto, type InvoiceAttemptStage } from '../../lib/clients/invoice-attempts.js'

interface Props {
  orderId: string
}

const STAGE_META: Record<InvoiceAttemptStage, { label: string; tone: 'info' | 'success' | 'warn' | 'error'; Icon: typeof Eye }> = {
  ticket_viewed: { label: 'Cliente abrió el ticket', tone: 'info', Icon: Eye },
  invoice_form_submitted: { label: 'Envió datos fiscales', tone: 'info', Icon: FileEdit },
  contact_upserted: { label: 'Cliente fiscal registrado', tone: 'success', Icon: UserPlus },
  stamp_success: { label: 'Factura timbrada', tone: 'success', Icon: CheckCircle2 },
  stamp_error: { label: 'Error al timbrar', tone: 'error', Icon: XCircle },
  system_error: { label: 'Error de sistema', tone: 'error', Icon: AlertOctagon },
}

function fmtTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString('es-MX', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return iso
  }
}

export default function InvoiceAttemptsTimeline({ orderId }: Props) {
  const [attempts, setAttempts] = useState<InvoiceAttemptDto[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  function load(signal?: AbortSignal): void {
    setLoading(true)
    setError(null)
    listInvoiceAttempts({ posOrderId: orderId, limit: 100 }, signal)
      .then((items) => setAttempts(items))
      .catch((e) => setError(e instanceof Error ? e.message : 'No se pudo cargar el historial'))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    const ctrl = new AbortController()
    load(ctrl.signal)
    return () => ctrl.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId])

  // Caso vacío: silencio total. Sólo loading inicial muestra placeholder.
  if (loading && attempts === null) {
    return null
  }
  if (!loading && (attempts === null || attempts.length === 0)) {
    return null
  }

  const list = attempts ?? []
  const hasRecentError = list.some(
    (a) =>
      (a.stage === 'stamp_error' || a.stage === 'system_error') &&
      Date.now() - new Date(a.createdAt).getTime() < 24 * 60 * 60 * 1000,
  )

  return (
    <section className="dhk8-iat" aria-label="Historial de facturación">
      <header className="dhk8-iat__head">
        <h3>Historial de facturación</h3>
        <button type="button" className="dhk8-iat__refresh" onClick={() => load()} aria-label="Recargar historial">
          <RefreshCw size={14} aria-hidden />
        </button>
      </header>

      {hasRecentError && (
        <div className="dhk8-iat__banner" role="alert">
          <AlertTriangle size={16} aria-hidden />
          <div>
            <strong>Un cliente intentó facturar y falló.</strong>
            <p>Revisa el último error abajo. Si el cliente lo reporta, ya tienes el contexto.</p>
          </div>
        </div>
      )}

      {error && <div className="dhk8-iat__err" role="alert">{error}</div>}

      <ol className="dhk8-iat__list">
        {list.map((a) => {
          const meta = STAGE_META[a.stage] ?? STAGE_META.system_error
          const Icon = meta.Icon
          const rfc = a.receptorJson?.rfc
          const razon = a.receptorJson?.razonSocial
          return (
            <li key={a.id} className={`dhk8-iat__item dhk8-iat__item--${meta.tone}`}>
              <span className="dhk8-iat__ico" aria-hidden>
                <Icon size={14} />
              </span>
              <div className="dhk8-iat__body">
                <div className="dhk8-iat__row1">
                  <span className="dhk8-iat__stage">{meta.label}</span>
                  <span className="dhk8-iat__time">{fmtTime(a.createdAt)}</span>
                </div>
                {(rfc || razon) && (
                  <div className="dhk8-iat__row2">
                    {rfc && <code className="dhk8-iat__rfc">{rfc}</code>}
                    {razon && <span className="dhk8-iat__legal">{razon}</span>}
                  </div>
                )}
                {a.cfdiUuid && (
                  <div className="dhk8-iat__row2">
                    <span className="dhk8-iat__label">UUID SAT:</span>
                    <code className="dhk8-iat__uuid">{a.cfdiUuid}</code>
                  </div>
                )}
                {a.errorMessage && (
                  <div className="dhk8-iat__row2 dhk8-iat__err-detail">
                    {a.errorCode && <code className="dhk8-iat__code">{a.errorCode}</code>}
                    <span>{a.errorMessage}</span>
                  </div>
                )}
                {a.env && (
                  <div className="dhk8-iat__row2 dhk8-iat__env">
                    Ambiente: <strong>{a.env === 'live' ? 'Vivo' : 'Pruebas'}</strong>
                  </div>
                )}
              </div>
            </li>
          )
        })}
      </ol>
      <style>{styles}</style>
    </section>
  )
}

const styles = `
.dhk8-iat { margin: var(--pos-space-4, 16px) 0; padding: var(--pos-space-3, 12px); background: var(--pos-surface, #161C18); border-radius: var(--pos-radius-md, 8px); border: 1px solid var(--pos-separator-strong, rgba(255,255,255,.14)); }
.dhk8-iat__head { display: flex; justify-content: space-between; align-items: center; margin-bottom: var(--pos-space-3, 12px); }
.dhk8-iat__head h3 { margin: 0; font: inherit; font-size: var(--pos-text-md, 15px); font-weight: var(--pos-weight-semibold, 600); color: var(--pos-fg, #E8EFE9); }
.dhk8-iat__refresh { border: none; background: transparent; color: var(--pos-fg-tertiary, #7E8C82); cursor: pointer; padding: var(--pos-space-1, 4px); border-radius: var(--pos-radius-sm, 6px); }
.dhk8-iat__refresh:hover { background: var(--pos-elevated, #1E2520); color: var(--pos-fg, #E8EFE9); }
.dhk8-iat__banner { display: flex; gap: var(--pos-space-2, 8px); align-items: flex-start; padding: var(--pos-space-2, 8px) var(--pos-space-3, 12px); margin-bottom: var(--pos-space-3, 12px); background: color-mix(in oklch, var(--pos-warning, #E0A23C) 14%, transparent); border: 1px solid color-mix(in oklch, var(--pos-warning, #E0A23C) 40%, transparent); border-radius: var(--pos-radius-md, 8px); color: var(--pos-warning, #E0A23C); }
.dhk8-iat__banner strong { display: block; font-size: var(--pos-text-sm, 13px); color: var(--pos-fg, #E8EFE9); }
.dhk8-iat__banner p { margin: var(--pos-space-1, 4px) 0 0; font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-secondary, #B6BFB8); }
.dhk8-iat__err { padding: var(--pos-space-2, 8px); color: var(--pos-danger, #E5484D); font-size: var(--pos-text-sm, 13px); }
.dhk8-iat__list { list-style: none; padding: 0; margin: 0; display: flex; flex-direction: column; gap: var(--pos-space-2, 8px); }
.dhk8-iat__item { display: flex; gap: var(--pos-space-2, 8px); padding: var(--pos-space-2, 8px); background: var(--pos-elevated, #1E2520); border-radius: var(--pos-radius-sm, 6px); align-items: flex-start; }
.dhk8-iat__item--success .dhk8-iat__ico { color: var(--pos-tint, #1F8B3A); }
.dhk8-iat__item--warn .dhk8-iat__ico { color: var(--pos-warning, #E0A23C); }
.dhk8-iat__item--error .dhk8-iat__ico { color: var(--pos-danger, #E5484D); }
.dhk8-iat__item--info .dhk8-iat__ico { color: var(--pos-fg-tertiary, #7E8C82); }
.dhk8-iat__ico { padding-top: 2px; }
.dhk8-iat__body { flex: 1; display: flex; flex-direction: column; gap: var(--pos-space-1, 4px); }
.dhk8-iat__row1 { display: flex; justify-content: space-between; gap: var(--pos-space-2, 8px); align-items: baseline; flex-wrap: wrap; }
.dhk8-iat__stage { font-size: var(--pos-text-sm, 13px); font-weight: var(--pos-weight-medium, 500); color: var(--pos-fg, #E8EFE9); }
.dhk8-iat__time { font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-tertiary, #7E8C82); font-variant-numeric: tabular-nums; }
.dhk8-iat__row2 { font-size: var(--pos-text-xs, 12px); color: var(--pos-fg-secondary, #B6BFB8); display: flex; gap: var(--pos-space-2, 8px); flex-wrap: wrap; align-items: baseline; }
.dhk8-iat__rfc, .dhk8-iat__uuid, .dhk8-iat__code { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; font-size: var(--pos-text-xs, 12px); padding: 1px 6px; background: rgba(255,255,255,.06); border-radius: 4px; color: var(--pos-fg, #E8EFE9); }
.dhk8-iat__legal { color: var(--pos-fg, #E8EFE9); }
.dhk8-iat__label { color: var(--pos-fg-tertiary, #7E8C82); }
.dhk8-iat__err-detail { color: var(--pos-danger, #E5484D); }
.dhk8-iat__env { font-size: 11px; }
.dhk8-iat__env strong { color: var(--pos-fg, #E8EFE9); }
`
